import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { httpClient } from './httpClient';

/**
 * Polityka haseł, którą EGZEKWUJE serwer — czytana z `GET /api/auth/policy`, nigdy ze stałej.
 *
 * ⭐ Dlaczego to nie jest stała (2026-08-22, pierwsza aktywacja konta na produkcji): front miał
 * `MIN_PASSWORD_LENGTH = 8` — „lustro reguły serwera" — a serwer wymagał 10
 * (`Identity:MinimumPasswordLength`). Klient wpisał 8 znaków: formularz przepuścił, API odmówiło,
 * komunikat brzmiał „co najmniej 8". Wpisał 9 — to samo zdanie. Podpowiedź, walidacja i odmowa
 * stały na liczbie, na której NIE stało zachowanie. Kopia reguły rozjeżdża się z oryginałem
 * po cichu i w najgorszym możliwym miejscu — przy pierwszym wejściu klienta.
 *
 * Zasada dec. 96 zostaje („tekst czyta to samo źródło, co zachowanie"); zmienia się tylko to,
 * gdzie źródło stoi: po drugiej stronie drutu. Formularze czytają ten hook; odmowa serwera niesie
 * `args.minLength` (fakt o niepowodzeniu), więc komunikat błędu też nie ma własnej liczby.
 *
 * ⚠ Dopóki polityka nie dojedzie (`data === undefined`), formularz ma NIE udawać, że zna próg:
 * bez podpowiedzi z liczbą i bez walidacji długości po stronie klienta. Serwer i tak rozstrzyga.
 * ⛔ Żadnej wartości domyślnej „na wszelki wypadek" — to właśnie ona była błędem.
 */
const passwordPolicySchema = z.object({
  minPasswordLength: z.number().int().positive(),
});

export type PasswordPolicy = z.infer<typeof passwordPolicySchema>;

export async function fetchPasswordPolicy(signal?: AbortSignal): Promise<PasswordPolicy> {
  const response = await httpClient.get<unknown>('/auth/policy', {
    // ⚠ `anonymous`: czytają to formularze kont, które jeszcze nie istnieją (aktywacja,
    //   rejestracja) — 401 z tej trasy nie może wysyłać nikogo na logowanie.
    anonymous: true,
    ...(signal ? { signal } : {}),
  });

  return passwordPolicySchema.parse(response);
}

/** Ten sam klucz w każdym formularzu — polityka jest jedna na host, więc jedno pobranie wystarcza. */
export const passwordPolicyQueryKey = ['auth', 'policy'] as const;

export function usePasswordPolicy() {
  return useQuery({
    queryKey: passwordPolicyQueryKey,
    queryFn: ({ signal }) => fetchPasswordPolicy(signal),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
